import { FC, useEffect, useRef } from 'react';
import ExpiredToken from 'components/shared/ExpiredToken';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import FlightDetailsSkeleton from './FlightDetailsSkeleton';
import useFlight from 'hooks/useFlightDetails';
import FlightDetails from './FlightDetails';
import TotalDetails from './TotalDetails';
import { saveScreenshot } from 'utils/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import { useTranslation } from 'react-i18next';

const FlightPage: FC = () => {
  const location = useLocation();
  const [urlParams] = useSearchParams();
  const idbKey = location.pathname + location.search;
  const { flexFlightId, response, flexOrFlight, bounds, setFlexFlightId } = useFlight(idbKey);
  const navigate = useNavigate();
  const detailsRef = useRef<HTMLDivElement>(null);
  const [t] = useTranslation();
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => saveState());
  const stateRef = useRef<any>();
  stateRef.current = {
    flexFlightId,
    bounds,
    response,
  };
  useGetPage('Flights/Details');

  useEffect(() => {
    if (response) {
      setTimeout(() => {
        saveScreenshot(detailsRef.current, 'Details', 'Flights');
      }, 1000);
    }
  }, [response]);

  const onContinue = async () => {
    if (!flexOrFlight) return;
    if (response?.saleSearchToken) {
      const searchParams = new URLSearchParams({
        id: flexFlightId || flexOrFlight.id,
        searchToken: response?.saleSearchToken,
        isSale: 'true',
      });

      navigate({ pathname: '/flight/book', search: searchParams.toString() });
    } else {
      const searchParams = new URLSearchParams({
        id: flexFlightId || flexOrFlight.id,
        searchToken: urlParams.get('searchToken') || '',
      });

      navigate({ pathname: '/flight/book', search: searchParams.toString() });
    }

    saveState();
  };
  const saveState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <div className="details-wrapper" ref={detailsRef}>
      <div className="details flight-details">
        {window.history.length > 1 && (
          <div className="mb-20 default-shadow flex-between dyn-change-header">
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={() => onClickBack('flight', 'results')}
              className="btn btn-link"
            />
            <Button
              title={t('search.new')}
              icon={faSearch}
              onClick={() => onClickSearch('flight', '/flights')}
              className="btn btn-link"
            />
          </div>
        )}
        <FlightDetails
          flexFlightId={flexFlightId}
          response={response}
          flexOrFlight={flexOrFlight}
          setFlexFlightId={setFlexFlightId}
          bounds={bounds}
          googleMapOfAirports={response?.googleMapOfAirports}
          listFlexFlights={response?.flexDetails?.listOfFlexFlights}
          searchToken={urlParams.get('searchToken') || ''}
        />
        {flexOrFlight && <TotalDetails flight={flexOrFlight} onContinue={onContinue} />}

        {response?.sessionExpired && <ExpiredToken storePath="flight" />}

        {!response && <FlightDetailsSkeleton />}
      </div>
    </div>
  );
};

export default withErrorBoundary(FlightPage, 'FlightPage');
